import React, { FC, useCallback, useEffect, useState } from "react";

import {
    OnboardingAside,
    OnboardingContent,
    Place,
    PlacesAutocomplete,
    PrimaryAlternateControl,
} from "@xapp/components";

import style from "./AddressStep.module.scss";

export interface AddressStepProps {
    /**
     * Optional aside content
     */
    aside?: React.ReactNode;
    /**
     * User has confirmed a place with name and address.  The place will be on the new props.
     */
    onPlaceSelected(place: Place): void;
    /**
     *
     * @param search
     */
    onUseAdvancedSearch?(search?: string): void;
    /**
     * Will add an optional button that allows the user to use the website instead of Google Business Profile
     */
    onUseWebsite(): void;
}

/**
 * V3 of our AddressStep, just asks to find the Google Business Profile
 */
export const AddressStep: FC<AddressStepProps> = (props) => {
    const { onPlaceSelected, onUseWebsite, aside, onUseAdvancedSearch } = props;

    const [place, setPlace] = useState<Place | null>(null);

    const [hitTimeout, setHitTimeout] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string | number>("");

    // very simple timeout that starts when the user has started typing something
    useEffect(() => {
        if (searchValue && hitTimeout === false) {
            const timeout = setTimeout(() => {
                setHitTimeout(true);
            }, 2000);
            return () => clearTimeout(timeout);
        }
    }, [hitTimeout, searchValue]);

    const handlePlace = (placeId: string) => {
        setPlace({ placeId });
        // just continue? i think so
        onPlaceSelected({ placeId });
    };

    const handleUseAdvancedSearch = useCallback(() => {
        if (onUseAdvancedSearch) {
            onUseAdvancedSearch(`${searchValue}`);
        }
    }, [onUseAdvancedSearch, searchValue]);

    const handleContinueClicked = useCallback(() => {
        if (place) {
            onPlaceSelected(place);
        } else if (onUseAdvancedSearch) {
            onUseAdvancedSearch(`${searchValue}`);
        }
    }, [onPlaceSelected, onUseAdvancedSearch, place, searchValue]);

    let disableContinue = true;
    if (typeof onUseAdvancedSearch === "function") {
        // if we have a place, just move along, otherwise make sure we have hit the timeout and have a search value
        if (place) {
            disableContinue = false;
        } else {
            disableContinue = !hitTimeout || !searchValue;
        }
    } else {
        // if we have a place, just move along, otherwise make sure we have a place
        disableContinue = !place || !place.placeId;
    }

    return (
        <>
            {aside && <OnboardingAside>{aside}</OnboardingAside>}

            <OnboardingContent>
                <div className={style.addressViewWrapper}>
                    <div className={style.imageWrapper}>
                        <img
                            src={"/img/google-business-profile-logo.jpg"}
                            alt="logo"
                            style={{ maxHeight: "50px", width: "auto", display: "block" }}
                        />
                    </div>
                    <div style={{ textAlign: "center", marginBottom: "20px" }}>
                        <h3>Find your Google Business Profile</h3>
                    </div>
                    <div className={style.placeWrapper}>
                        <PlacesAutocomplete
                            onSelect={handlePlace}
                            onChange={setSearchValue}
                            placeholder={"Type your business name and city..."}
                            onUseAdvancedSearch={handleUseAdvancedSearch}
                            config={{
                                advancedSearchText: "Can't find your business? Try Advanced Search",
                            }}
                        />
                    </div>
                </div>

                <PrimaryAlternateControl
                    labels={{ primary: "Continue ➡️", alternate: "Use Website" }}
                    ids={{ primary: "lookupProfile", alternate: "lookupWebsite" }}
                    disabled={{ primary: disableContinue }}
                    onPrimary={handleContinueClicked}
                    onAlternate={onUseWebsite}
                />
            </OnboardingContent>
        </>
    );
};
